import { useState } from 'react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import { Select } from '@/shared/ui/select';
import { ErrorState } from '@/shared/feedback';
import { describeAgreementFailure } from '@/modules/agreements/shared';
import { useSignatureUpload } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signature-upload';
import type { SignatureMimeType } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signature-upload';
import type { InteractiveSigningMethod } from '@/modules/agreements/subject/pages/signing-page/hooks/use-signing';
import type { SigningSessionResponse } from '@/api/schemas/agreements.types';
import { SignatureFileInput } from '@/modules/agreements/subject/pages/signing-page/parts/signature-file-input';

const MIME_OPTIONS = [
  { value: 'image/png', label: 'PNG' },
  { value: 'image/jpeg', label: 'JPEG' },
];

/**
 * Capture a signature image and upload it to the object store.
 *
 * A captured image is a SIGNATURE IMAGE. It is never described as a certificate-backed
 * digital signature, and uploading it does not complete the execution — the signer must
 * still verify and sign.
 */
export function SignatureCapture(props: {
  executionId: string;
  ensureSession: (method: InteractiveSigningMethod) => Promise<SigningSessionResponse>;
  signingMethod: InteractiveSigningMethod;
  onUploaded: (objectKey: string, mimeType: SignatureMimeType, byteSize: number) => void;
}): ReactNode {
  const [mimeType, setMimeType] = useState<SignatureMimeType>('image/png');
  const upload = useSignatureUpload({
    executionId: props.executionId,
    ensureSession: props.ensureSession,
    signingMethod: props.signingMethod,
    mimeType,
    onUploaded: props.onUploaded,
  });

  return (
    <div className="space-y-4">
      {upload.error ? (
        <ErrorState
          title="Could not upload the signature"
          message={describeAgreementFailure(upload.error)}
        />
      ) : null}
      <Select
        label="Image format"
        options={MIME_OPTIONS}
        value={mimeType}
        onChange={(event) => setMimeType(event.target.value as SignatureMimeType)}
      />
      <SignatureFileInput
        disabled={upload.busy}
        onFile={(file) => void upload.upload(file)}
      />
      {upload.uploadedKey ? (
        <p className="text-sm text-positive">
          Signature image uploaded. Continue to verification.
        </p>
      ) : null}
      {upload.busy ? (
        <Button loading disabled>
          Uploading
        </Button>
      ) : null}
    </div>
  );
}
